import { RootState } from 'data/reducer'
import { AppEvent, logAppEvent } from 'lib/analytics'
import { config } from 'lib/config'
import { i18n } from 'lib/i18n'
import { AnyAction } from 'redux'
import { StateObservable } from 'redux-observable'
import { filter, from, ignoreElements, map, Observable, switchMap } from 'rxjs'
import { withLatestFrom } from 'rxjs/operators'

import {
  fetchCollectionsAndPlaces,
  setCollections,
  setLastVisitedCollectionId,
  setPlaces,
} from '../../Collections/data/reducer'
import { addNotificationToQueue } from '../../Notifications'
import {
  hasValidNonce,
  signIn as signInHa,
  signOut as signOutHa,
  signUp as signUpHa,
} from '../lib/HereAccountService'
import { decodeOidcState, encodeOidcState } from '../lib/utils'
import { toError, toParams } from './epic.utils'
import {
  handleError,
  handleToken,
  HereAccount,
  removeUser,
  setOidcState,
  setUser,
  signIn,
  signOut,
  signUp,
} from './reducer'

export const handleErrorEpic = (
  action$: Observable<AnyAction>,
  _$: StateObservable<RootState>
): Observable<AnyAction> =>
  action$.pipe(
    filter(handleError.match),
    map((value) => toError(value.payload)),
    filter((value) => value.error !== '' && value.state !== ''),
    switchMap((value) =>
      from([setOidcState({ ...decodeOidcState(value.state), error: value.error })])
    )
  )

export const handleTokenEpic = (
  action$: Observable<AnyAction>,
  _$: StateObservable<RootState>
): Observable<AnyAction> =>
  action$.pipe(
    filter(handleToken.match),
    map((value) => toParams(value.payload)),
    filter((value) => value.accessToken !== undefined && value.idToken !== undefined),
    filter((value) => hasValidNonce(value.idToken)),
    switchMap((value) =>
      from(
        fetch(config.HA_USERINFO_URL, {
          headers: { Authorization: `Bearer ${value.accessToken}` },
        })
          .then((res) => (res.ok ? res.json() : Promise.reject(res.statusText)))
          .then((body) => {
            return {
              oidcState: decodeOidcState(value.state),
              user: {
                ...body,
                accessToken: value.accessToken,
                expires: Date.now() + Number.parseInt(value.expiresIn, 10) * 1_000,
              } as HereAccount,
            }
          })
          .catch((_) => {
            return Promise.resolve({
              oidcState: decodeOidcState(value.state),
              user: null,
            })
          })
      )
    ),
    switchMap((value) => {
      if (value.user === null) {
        return from([
          removeUser(),
          setOidcState(value.oidcState),
          setLastVisitedCollectionId(undefined),
          setCollections({}),
          setPlaces({}),
        ])
      }

      return from([setUser(value.user), setOidcState(value.oidcState), fetchCollectionsAndPlaces()])
    })
  )

export const signUpEpic = (action$: Observable<AnyAction>): Observable<AnyAction> =>
  action$.pipe(
    filter(signUp.match),
    switchMap((action) => logAppEvent(AppEvent.AccountCreationStarted).then(() => action)),
    switchMap((action) => {
      signUpHa(encodeOidcState(action.payload))
      return Promise.resolve()
    }),
    ignoreElements()
  )

export const logHaActionOutcomeEpic = (action$: Observable<AnyAction>): Observable<AnyAction> =>
  action$.pipe(
    filter(setOidcState.match),
    map((value) => value.payload),
    filter((value) => value.action !== ''),
    switchMap((value) => {
      if (value.error === '') {
        const event =
          value.action === 'sign-in'
            ? AppEvent.AccountSignInSucceeded
            : AppEvent.AccountCreationSucceeded
        return logAppEvent(event)
      }

      const event =
        value.action === 'sign-in' ? AppEvent.AccountSignInFailed : AppEvent.AccountCreationFailed
      return logAppEvent(event, { error: value.error })
    }),
    ignoreElements()
  )

export const signInEpic = (action$: Observable<AnyAction>): Observable<AnyAction> =>
  action$.pipe(
    filter(signIn.match),
    switchMap((action) => from(logAppEvent(AppEvent.AccountSignInStarted).then(() => action))),
    switchMap((action) => {
      signInHa(encodeOidcState(action.payload), 'login')
      return Promise.resolve()
    }),
    ignoreElements()
  )

export const signOutEpic = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
): Observable<AnyAction> =>
  action$.pipe(
    filter(signOut.match),
    switchMap(() => from(logAppEvent(AppEvent.AccountSignOut))),
    withLatestFrom(state$),
    switchMap(([_, state]) => from(signOutHa(state?.account?.user?.accessToken))),
    switchMap(() =>
      from([
        removeUser(),
        addNotificationToQueue({
          content: i18n('account.menu.notification.sign-out-success'),
          duration: 5,
          inline: true,
          variant: 'confirmation',
        }),
        setCollections({}),
        setPlaces({}),
        setLastVisitedCollectionId(undefined),
      ])
    )
  )
